/**
 * Google Geocoding + Maps Static + Places autocomplete helpers for ADU site planner.
 * See docs/ADU_SITE_PLANNER.md
 */

const MILES_TO_METERS = 1609.344;
const DEFAULT_GROUND_SQ_MI = 0.2; // property-scale; panel ticker 0.1–0.3
const MIN_GROUND_SQ_MI = 0.1;
const MAX_GROUND_SQ_MI = 0.3;

/** Side length (m) of a square covering `sqMi` square miles. */
export function sqMiToSideMeters(sqMi) {
  return Math.sqrt(Number(sqMi) || DEFAULT_GROUND_SQ_MI) * MILES_TO_METERS;
}

export function sideMetersToSqMi(meters) {
  const side = Number(meters) || 0;
  return (side * side) / (MILES_TO_METERS * MILES_TO_METERS);
}

/** US customary label for view coverage (e.g. "0.20 sq mi"). */
export function formatGroundCoverageSqMi(sqMi, digits = 2) {
  return `${(Number(sqMi) || 0).toFixed(digits)} sq mi`;
}

const DEFAULT_GROUND_METERS = sqMiToSideMeters(DEFAULT_GROUND_SQ_MI);
const STATIC_MAP_SIZE = 640;

let mapsPlacesPromise = null;

function getApiKey() {
  const key = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
  if (!key) {
    throw new Error(
      'Missing REACT_APP_GOOGLE_MAPS_API_KEY. Add it to .env.local (docs/ADU_SITE_PLANNER.md).'
    );
  }
  return key;
}

/**
 * Load Maps JavaScript API with the Places library (once).
 * Required for client-side autocomplete (Places REST is not CORS-friendly).
 */
export function loadGoogleMapsPlaces() {
  if (typeof window === 'undefined') {
    return Promise.reject(new Error('Places autocomplete requires a browser'));
  }
  if (window.google?.maps?.places) {
    return Promise.resolve(window.google.maps);
  }
  if (mapsPlacesPromise) return mapsPlacesPromise;

  const key = getApiKey();
  mapsPlacesPromise = new Promise((resolve, reject) => {
    const callbackName = `__construxGmapsPlacesInit`;
    window[callbackName] = () => {
      try {
        delete window[callbackName];
        if (!window.google?.maps?.places) {
          reject(new Error('Maps JS loaded but Places library missing'));
          return;
        }
        resolve(window.google.maps);
      } catch (err) {
        reject(err);
      }
    };
    const existing = document.querySelector('script[data-construx-gmaps-places]');
    if (existing) return;
    const script = document.createElement('script');
    script.dataset.construxGmapsPlaces = '1';
    script.async = true;
    script.defer = true;
    script.src = `https://maps.googleapis.com/maps/api/js?key=${encodeURIComponent(
      key
    )}&libraries=places&callback=${callbackName}`;
    script.onerror = () => {
      mapsPlacesPromise = null;
      reject(
        new Error(
          'Failed to load Maps JavaScript API. Enable Maps JavaScript API + Places API for this key.'
        )
      );
    };
    document.head.appendChild(script);
  });
  return mapsPlacesPromise;
}

/**
 * @param {string} input
 * @returns {Promise<Array<{ description: string, placeId: string }>>}
 */
export async function fetchAddressSuggestions(input) {
  const q = (input || '').trim();
  if (q.length < 3) return [];

  const maps = await loadGoogleMapsPlaces();
  const service = new maps.places.AutocompleteService();

  return new Promise((resolve) => {
    service.getPlacePredictions(
      {
        input: q,
        types: ['address'],
      },
      (predictions, status) => {
        if (
          status !== maps.places.PlacesServiceStatus.OK ||
          !predictions?.length
        ) {
          resolve([]);
          return;
        }
        resolve(
          predictions.map((p) => ({
            description: p.description,
            placeId: p.place_id,
          }))
        );
      }
    );
  });
}

/**
 * Open-Meteo elevation fallback (CORS-friendly) when Google Elevation is denied.
 * Docs: docs/ADU_SITE_PLANNER.md
 */
async function fetchOpenMeteoElevation(lat, lng) {
  const url = `https://api.open-meteo.com/v1/elevation?latitude=${lat}&longitude=${lng}`;
  const res = await fetch(url);
  if (!res.ok) throw new Error(`Open-Meteo elevation HTTP ${res.status}`);
  const data = await res.json();
  const elev = data?.elevation?.[0];
  if (elev == null || !Number.isFinite(elev)) {
    throw new Error('Open-Meteo returned no elevation');
  }
  return elev;
}

/**
 * Ground elevation (meters above sea level) for Photorealistic reorientation.
 * Tries Maps JS ElevationService, then Open-Meteo.
 * @returns {Promise<number>}
 */
export async function fetchGroundElevation(lat, lng) {
  try {
    const maps = await loadGoogleMapsPlaces();
    if (!maps.ElevationService) {
      throw new Error('ElevationService unavailable — enable Elevation API for this key.');
    }
    const service = new maps.ElevationService();
    const elev = await new Promise((resolve, reject) => {
      service.getElevationForLocations(
        { locations: [{ lat, lng }] },
        (results, status) => {
          if (status !== 'OK' || results?.[0]?.elevation == null) {
            reject(
              new Error(
                status === 'OK'
                  ? 'Elevation returned no results'
                  : `Elevation failed: ${status}`
              )
            );
            return;
          }
          resolve(results[0].elevation);
        }
      );
    });
    return elev;
  } catch (googleErr) {
    console.warn('[Site] Google Elevation failed, trying Open-Meteo', googleErr);
    return fetchOpenMeteoElevation(lat, lng);
  }
}

/**
 * @param {string} address
 * @returns {Promise<{ lat: number, lng: number, formattedAddress: string, locationType?: string }>}
 */
export async function geocodeAddress(address) {
  const key = getApiKey();
  const url = new URL('https://maps.googleapis.com/maps/api/geocode/json');
  url.searchParams.set('address', address);
  url.searchParams.set('key', key);

  const res = await fetch(url.toString());
  if (!res.ok) throw new Error(`Geocoding HTTP ${res.status}`);
  const data = await res.json();
  if (data.status !== 'OK' || !data.results?.[0]) {
    throw new Error(data.error_message || `Geocoding failed: ${data.status}`);
  }
  const result = data.results[0];
  const { lat, lng } = result.geometry.location;
  return {
    lat,
    lng,
    formattedAddress: result.formatted_address,
    locationType: result.geometry.location_type,
  };
}

/** Web Mercator meters-per-pixel at lat / zoom */
export function metersPerPixel(lat, zoom) {
  return (
    (156543.03392 * Math.cos((lat * Math.PI) / 180)) / Math.pow(2, zoom)
  );
}

export function zoomForGroundMeters(lat, groundMeters, mapSizePx = STATIC_MAP_SIZE) {
  const mpp = groundMeters / mapSizePx;
  const z =
    Math.log2((156543.03392 * Math.cos((lat * Math.PI) / 180)) / mpp);
  // Allow zoom ~12–21 so ~1 sq mi grounds are not forced too tight
  return Math.min(21, Math.max(12, Math.round(z)));
}

/**
 * Build Maps Static satellite URL (does not fetch).
 */
export function buildStaticSatelliteUrl({
  lat,
  lng,
  groundMeters = DEFAULT_GROUND_METERS,
  size = STATIC_MAP_SIZE,
  scale = 2,
}) {
  const key = getApiKey();
  const zoom = zoomForGroundMeters(lat, groundMeters, size);
  const url = new URL('https://maps.googleapis.com/maps/api/staticmap');
  url.searchParams.set('center', `${lat},${lng}`);
  url.searchParams.set('zoom', String(zoom));
  url.searchParams.set('size', `${size}x${size}`);
  url.searchParams.set('scale', String(scale));
  url.searchParams.set('maptype', 'satellite');
  url.searchParams.set('key', key);
  return { url: url.toString(), zoom, size, scale, groundMeters };
}

/**
 * Fetch satellite image as an object URL + ground extent in meters.
 * Caller should revokeObjectURL when done.
 */
export async function fetchSatelliteGround(lat, lng, groundMeters = DEFAULT_GROUND_METERS) {
  const built = buildStaticSatelliteUrl({ lat, lng, groundMeters });
  const res = await fetch(built.url);
  if (!res.ok) throw new Error(`Static Maps HTTP ${res.status}`);
  const blob = await res.blob();
  if (!blob.type.startsWith('image/')) {
    throw new Error('Static Maps returned non-image (check API key / billing).');
  }
  const objectUrl = URL.createObjectURL(blob);
  const actualGroundMeters =
    metersPerPixel(lat, built.zoom) * built.size;
  return {
    objectUrl,
    groundMeters: actualGroundMeters,
    lat,
    lng,
    zoom: built.zoom,
  };
}

export {
  DEFAULT_GROUND_METERS,
  DEFAULT_GROUND_SQ_MI,
  MIN_GROUND_SQ_MI,
  MAX_GROUND_SQ_MI,
  MILES_TO_METERS,
  STATIC_MAP_SIZE,
};
